import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Save, X, FileText, User, Briefcase, MessageSquare, Loader2, Building2, CreditCard
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import InvoiceItemsTable from '../components/ui/InvoiceItemsTable';
import { useToast } from '../hooks/useToast';
import {
    fetchPOSCustomers,
    fetchUtilStaff,
    fetchCatentriesUtil,
    fetchStoresWhereStaffAssigned,
    fetchStaffViewCreditline,
    readTaxes,
    fetchDiscountRules,
    client
} from '../apiHelper';

const EditInvoice = () => {
    const { ordersId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initial payload passed via navigation state
    const initialInvoiceData = location.state?.invoiceData || null;

    // Form state
    const [formData, setFormData] = useState({
        ordersId: ordersId || '',
        customerId: '',
        storeId: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().slice(0, 16),
        dueDate: '',
        salesPersonId: '',
        subject: '',
        status: 'HLD'
    });

    const [lineItems, setLineItems] = useState([]);
    const [overrideItems, setOverrideItems] = useState([]);

    // Dropdown state options
    const [customerOptions, setCustomerOptions] = useState([]);
    const [salesPersonOptions, setSalesPersonOptions] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);
    const [itemOptions, setItemOptions] = useState([]);
    const [taxOptions, setTaxOptions] = useState([]);
    const [discountOptions, setDiscountOptions] = useState([]);
    const [selectedStore, setSelectedStore] = useState('');

    // Credit line state
    const [creditline, setCreditline] = useState(null);
    const [loadingCreditline, setLoadingCreditline] = useState(false);

    // Loading indicators for components
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingStaff, setLoadingStaff] = useState(false);
    const [loadingStores, setLoadingStores] = useState(true);
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadingTaxes, setLoadingTaxes] = useState(true);
    const [loadingDiscounts, setLoadingDiscounts] = useState(true);

    // Check Authentication Session
    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) {
            setUser(JSON.parse(stored));
        } else {
            showToast('Please log in to edit invoices.', 'error');
            navigate('/login');
        }
    }, [showToast, navigate]);

    // Fetch Reference Data Options
    useEffect(() => {
        if (!user) return;

        const loadReferenceData = async () => {
            try {
                // Stores
                setLoadingStores(true);
                const stores = await fetchStoresWhereStaffAssigned(user);
                const mappedStores = (stores || []).map(s => ({
                    label: s.identifier || `Branch ${s.store_id}`,
                    value: s.store_id,
                    storeData: s
                }));
                setStoreOptions(mappedStores);
                setLoadingStores(false);

                // Sales Staff
                setLoadingStaff(true);
                const staff = await fetchUtilStaff(user);
                setSalesPersonOptions((staff || []).map(s => ({
                    label: `${s.user?.first_name || ''} ${s.user?.last_name || ''}`.trim(),
                    value: s.member_id,
                    staffData: s
                })));
                setLoadingStaff(false);

                // Catalog Items
                setLoadingItems(true);
                const items = await fetchCatentriesUtil(user);
                setItemOptions((items || []).map(item => ({
                    value: item.catentry_id,
                    label: item.productname,
                    price: parseFloat(item.listprice || 0),
                    currency: item.currency,
                    type: item.catalog_type,
                    total_quantity: item.total_quantity
                })));
                setLoadingItems(false);

                // Tax Rules
                setLoadingTaxes(true);
                const taxes = await readTaxes(user);
                setTaxOptions((taxes || []).map(tax => ({
                    label: `${tax.code} – ${tax.identifier} (${parseFloat(tax.rate).toFixed(1)}%)`,
                    value: tax.taxcategory_id.toString(),
                    rate: parseFloat(tax.rate),
                    code: tax.code
                })));
                setLoadingTaxes(false);

                // Discount Rules
                setLoadingDiscounts(true);
                const rules = await fetchDiscountRules({ ...user, offset: 0, limit: 1000 });
                setDiscountOptions((rules || []).map(rule => ({
                    label: `${rule.name} – ${rule.description}`,
                    value: rule.discountrule_id.toString(),
                    type: rule.discount_type,
                    valueAmount: parseFloat(rule.value),
                    appliesTo: rule.applies_to,
                    targetProductId: rule.target_product_id,
                    startDate: rule.start_date,
                    endDate: rule.end_date,
                    isActive: rule.is_active,
                    customers: rule.customers?.map(c => c.member_id) || [],
                    stores: rule.stores?.map(s => s.store_id) || [],
                })));
                setLoadingDiscounts(false);

            } catch (err) {
                showToast('Error loading form options.', 'error');
            }
        };

        loadReferenceData();
    }, [user, showToast]);

    // Fetch Customer List
    const fetchCustomers = useCallback(async (searchQuery = '') => {
        if (!user || !selectedStore) return;
        setLoadingCustomers(true);
        try {
            const customers = await fetchPOSCustomers({ ...user, store_id: selectedStore }, searchQuery);
            setCustomerOptions((customers || []).map(c => {
                const u = c.user || {};
                const fullName = `${u.first_name || ''} ${u.middle_name || ''} ${u.last_name || ''}`.trim() || 'Unnamed Customer';
                return {
                    label: `${fullName} (${u.credential?.logon_id || 'N/A'})`,
                    value: c.member_id,
                    customerData: c
                };
            }));
        } catch (err) {
            showToast('Failed to load customers.', 'error');
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, selectedStore, showToast]);

    useEffect(() => {
        if (user && selectedStore) {
            fetchCustomers();
        }
    }, [user, selectedStore, fetchCustomers]);

    // Load and Hydrate Existing Invoice Details
    useEffect(() => {
        if (!user) return;

        const hydrateInvoiceDetails = async () => {
            setLoadingDetails(true);
            try {
                let fullInvoice = initialInvoiceData;

                // Fetch from API only if initialInvoiceData wasn't passed at all
                if (!fullInvoice) {
                    const response = await client.getData(`/sales/invoices/${ordersId}`);
                    fullInvoice = response.data || response;
                }

                if (fullInvoice) {
                    const storeId = fullInvoice.store_id || fullInvoice.store?.store_id || '';
                    const customerId = fullInvoice.customer?.member_id || '';

                    setSelectedStore(storeId);
                    setFormData({
                        ordersId: fullInvoice.orders_id,
                        customerId: customerId,
                        storeId: storeId,
                        invoiceNumber: fullInvoice.idempotency_key || `INV-${fullInvoice.orders_id}`,
                        invoiceDate: fullInvoice.placed_on ? new Date(fullInvoice.placed_on).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
                        dueDate: fullInvoice.creditsale?.payment_due_date ? new Date(fullInvoice.creditsale.payment_due_date).toISOString().split('T')[0] : '',
                        salesPersonId: fullInvoice.sales_person_id || user.users_id,
                        subject: fullInvoice.subject || '',
                        status: fullInvoice.status || 'HLD'
                    });

                    // Safely handle order_items even if null/undefined
                    const itemsList = fullInvoice.order_items || [];
                    if (Array.isArray(itemsList)) {
                        const parsedItems = itemsList.map((item, index) => ({
                            id: index + 1,
                            itemId: item.catentry_id || item.product_id || '',
                            quantity: item.quantity || 1,
                            rate: item.unit_price || item.rate || 0.00,
                            discountId: item.discountrule_id ? item.discountrule_id.toString() : '',
                            taxId: item.taxcategory_id ? item.taxcategory_id.toString() : '',
                            amount: item.total || item.amount || 0.00
                        }));
                        setOverrideItems(parsedItems);
                    }
                }
            } catch (err) {
                console.error('API Error:', err);
                showToast(err.response?.data?.detail || 'Failed to load complete invoice details.', 'error');
            } finally {
                setLoadingDetails(false);
            }
        };

        hydrateInvoiceDetails();
    }, [ordersId, initialInvoiceData, user, showToast]);

    // Fetch Credit Line Information
    useEffect(() => {
        const checkCreditline = async () => {
            if (!formData.customerId || !formData.storeId || !user?.users_id) {
                setCreditline(null);
                return;
            }

            setLoadingCreditline(true);
            try {
                const payload = {
                    store_id: Number(formData.storeId),
                    customer_id: Number(formData.customerId),
                    referer_id: user.users_id
                };
                const result = await fetchStaffViewCreditline(payload);
                setCreditline(result || null);
            } catch {
                setCreditline(null);
            } finally {
                setLoadingCreditline(false);
            }
        };

        checkCreditline();
    }, [formData.customerId, formData.storeId, user?.users_id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'storeId') setSelectedStore(value);
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemsChange = (items) => {
        setLineItems(items);
    };

    const validateForm = () => {
        if (!formData.customerId) return 'Customer selection is required.';
        if (!formData.storeId) return 'Issuing branch is required.';
        if (!formData.invoiceDate) return 'Invoice date is required.';
        if (!formData.dueDate) return 'Due date is required.';
        if (lineItems.length === 0) return 'At least one line item must be added.';
        if (new Date(formData.dueDate) < new Date(formData.invoiceDate)) {
            return 'Due Date cannot precede the Invoice Date.';
        }
        return null;
    };

    const handleUpdateInvoice = async () => {
        const validationError = validateForm();
        if (validationError) {
            showToast(validationError, 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const enrichedItems = lineItems.map(item => {
                const selectedItem = itemOptions.find(opt => opt.value === item.itemId);
                const selectedDiscount = discountOptions.find(d => d.value === item.discountId);
                const selectedTax = taxOptions.find(t => t.value === item.taxId);

                const price = parseFloat(item.rate || selectedItem?.price || 0);
                const qty = parseFloat(item.quantity || 1);
                const subtotal = price * qty;

                let discountAmount = 0;
                if (selectedDiscount) {
                    const discountValue = parseFloat(selectedDiscount.valueAmount || 0);
                    if (selectedDiscount.type === 'percentage') {
                        discountAmount = subtotal * (discountValue / 100);
                    } else if (selectedDiscount.type === 'fixed') {
                        discountAmount = discountValue;
                    }
                }

                const taxableAmount = subtotal - discountAmount;
                const taxRate = (selectedTax?.rate || 0) / 100;
                const taxAmount = taxableAmount * taxRate;
                const total = taxableAmount + taxAmount;

                return {
                    product_id: item.itemId ? Number(item.itemId) : null,
                    quantity: qty,
                    unit_price: price,
                    subtotal: Number(subtotal.toFixed(2)),
                    discount_rule_id: item.discountId ? Number(item.discountId) : null,
                    discount_amount: Number(discountAmount.toFixed(2)),
                    tax_category_id: item.taxId ? Number(item.taxId) : null,
                    tax_amount: Number(taxAmount.toFixed(2)),
                    total: Number(total.toFixed(2))
                };
            });

            const invoiceSubtotal = enrichedItems.reduce((sum, i) => sum + i.subtotal, 0);
            const invoiceDiscountTotal = enrichedItems.reduce((sum, i) => sum + i.discount_amount, 0);
            const invoiceTaxTotal = enrichedItems.reduce((sum, i) => sum + i.tax_amount, 0);
            const invoiceTotal = invoiceSubtotal - invoiceDiscountTotal + invoiceTaxTotal;

            const updatePayload = {
                orders_id: Number(formData.ordersId),
                customer_id: Number(formData.customerId),
                store_id: Number(formData.storeId),
                sales_person_id: formData.salesPersonId ? Number(formData.salesPersonId) : null,
                invoice_number: formData.invoiceNumber,
                invoice_date: formData.invoiceDate,
                due_date: formData.dueDate,
                subject: formData.subject,
                status: formData.status,
                items: enrichedItems,
                invoice_subtotal: invoiceSubtotal,
                invoice_discount_total: invoiceDiscountTotal,
                invoice_tax_total: invoiceTaxTotal,
                invoice_total: invoiceTotal
            };

            console.log(updatePayload);

            const response = await client.postData('/sales/edit-invoice', updatePayload);

            showToast(response?.msg || 'Invoice updated successfully!', 'success');
            navigate('/sales/invoices');
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to update invoice. Please try again.';
            showToast(errorMsg, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loadingDetails) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen">
                <Loader2 className="animate-spin h-10 w-10 text-indigo-600 mb-2" />
                <p className="text-zinc-500 text-sm">Fetching invoice details...</p>
            </div>
        );
    }

    return (
        <div className="w-full mx-auto p-4 md:p-8 max-w-7xl bg-zinc-50/30 min-h-screen">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                {/* Left Form Content */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2">
                        <div>
                            <div className="flex items-center gap-2.5 mb-1">
                                <div className="p-2 bg-indigo-50 rounded-xl border border-indigo-100 text-indigo-600 shadow-sm">
                                    <FileText size={22} />
                                </div>
                                <h1 className="text-2xl font-bold tracking-tight text-zinc-900">
                                    Edit Invoice #{formData.invoiceNumber}
                                </h1>
                            </div>
                            <p className="text-zinc-500 text-sm pl-1">Update invoice details, amounts, and associated items.</p>
                        </div>
                    </div>

                    {/* Context */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm rounded-xl bg-white">
                        <div className="flex items-center gap-2 mb-5 pb-3 border-b border-zinc-100">
                            <Building2 size={16} className="text-zinc-400" />
                            <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-wider">Sales Context</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <SearchableSelectBox
                                name="storeId"
                                label="Issuing Branch"
                                required
                                value={formData.storeId}
                                onChange={handleInputChange}
                                options={storeOptions}
                                loading={loadingStores}
                                placeholder="Select branch..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                            <SearchableSelectBox
                                name="salesPersonId"
                                label="Sales Person"
                                value={formData.salesPersonId}
                                onChange={handleInputChange}
                                isDisabled={true}
                                options={salesPersonOptions}
                                loading={loadingStaff}
                                placeholder="Select sales person..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    </Card>

                    {/* Customer */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm rounded-xl bg-white space-y-6">
                        <div className="flex items-center gap-2 mb-2">
                            <User size={18} className="text-indigo-600" />
                            <h3 className="font-semibold text-sm text-zinc-800">Target Customer</h3>
                        </div>
                        <SearchableSelectBox
                            name="customerId"
                            label="Customer"
                            required
                            value={formData.customerId}
                            onChange={handleInputChange}
                            options={customerOptions}
                            onSearch={fetchCustomers}
                            loading={loadingCustomers}
                            placeholder="Search customer..."
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </Card>

                    {/* Dates */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm rounded-xl bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <Input
                                name="invoiceNumber"
                                label="Invoice Number"
                                value={formData.invoiceNumber}
                                onChange={handleInputChange}
                            />
                            <Input
                                type="datetime-local"
                                name="invoiceDate"
                                label="Invoice Date"
                                required
                                value={formData.invoiceDate}
                                onChange={handleInputChange}
                            />
                            <Input
                                type="date"
                                name="dueDate"
                                label="Due Date"
                                required
                                value={formData.dueDate}
                                onChange={handleInputChange}
                            />
                        </div>
                    </Card>

                    {/* Notes */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm rounded-xl bg-white">
                        <div className="flex items-center gap-2 mb-4">
                            <MessageSquare size={16} className="text-zinc-400" />
                            <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-wider">Additional Notes</h3>
                        </div>
                        <Input
                            name="subject"
                            label="Subject (Optional)"
                            placeholder="Brief description visible to the customer"
                            value={formData.subject}
                            onChange={handleInputChange}
                        />
                    </Card>

                    {/* Items Table */}
                    <div className="space-y-3 pt-2">
                        <div className="flex items-center justify-between px-1">
                            <h3 className="font-bold text-sm text-zinc-800 flex items-center gap-2">
                                <Briefcase size={16} className="text-indigo-600" /> Line Items
                            </h3>
                            <span className="text-[11px] text-zinc-400 bg-zinc-100 border border-zinc-200/60 px-2 py-0.5 rounded-full font-medium">
                                Currency: ₦
                            </span>
                        </div>

                        {loadingItems || loadingTaxes || loadingDiscounts ? (
                            <Card className="p-12 border-zinc-200/60 text-center">
                                <Loader2 className="animate-spin h-8 w-8 mx-auto text-indigo-600 mb-3" />
                                <p className="text-zinc-500 text-xs font-medium">Loading catalog components...</p>
                            </Card>
                        ) : (
                            <div className="border border-zinc-200/60 shadow-xs rounded-xl bg-white overflow-hidden">
                                <InvoiceItemsTable
                                    itemOptions={itemOptions}
                                    taxOptions={taxOptions}
                                    discountOptions={discountOptions}
                                    currencySymbol="₦"
                                    onItemsChange={handleItemsChange}
                                    customerId={formData.customerId}
                                    storeId={formData.storeId}
                                    allItems={lineItems}
                                    overrideItems={overrideItems}
                                />
                            </div>
                        )}
                    </div>

                    {/* Action Controls */}
                    <div className="flex flex-col-reverse sm:flex-row justify-end items-center gap-3.5 pt-6 border-t border-zinc-200/80">
                        <Button
                            variant="ghost"
                            className="w-full sm:w-auto flex items-center justify-center text-zinc-600 hover:text-zinc-900 font-medium"
                            onClick={() => navigate(-1)}
                            disabled={isSubmitting}
                        >
                            <X size={16} className="mr-2" /> Cancel
                        </Button>
                        <Button
                            className="w-full sm:w-auto flex items-center justify-center font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md transition-all px-6"
                            variant="primary"
                            onClick={handleUpdateInvoice}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 size={16} className="mr-2 animate-spin" /> Updating...
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="mr-2" /> Save Changes
                                </>
                            )}
                        </Button>
                    </div>

                </div>

                {/* Right Side - Credit Status */}
                <div className="lg:col-span-1 lg:sticky lg:top-6">
                    <Card className="p-6 border-zinc-200/60 shadow-sm rounded-xl bg-white relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-[3px] bg-indigo-600" />
                        <div className="flex items-center justify-between mb-5 pb-3 border-b border-zinc-100">
                            <div className="flex items-center gap-2">
                                <CreditCard size={18} className="text-indigo-600" />
                                <h3 className="font-bold text-xs text-zinc-700 uppercase tracking-wide">Credit Status</h3>
                            </div>
                        </div>

                        {loadingCreditline ? (
                            <div className="text-center py-12">
                                <Loader2 className="animate-spin h-8 w-8 mx-auto text-indigo-600" />
                            </div>
                        ) : creditline ? (
                            <div className="space-y-5">
                                <div className="p-3.5 bg-zinc-50 border border-zinc-100 rounded-lg">
                                    <p className="text-[11px] font-bold uppercase text-zinc-400">Credit Limit</p>
                                    <p className="text-2xl font-extrabold text-zinc-900">
                                        ₦{Number(creditline.credit_limit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </p>
                                </div>
                                <div className="p-3.5 bg-zinc-50 border border-zinc-100 rounded-lg">
                                    <p className="text-[11px] font-bold uppercase text-zinc-400">Current Balance</p>
                                    <p className="text-2xl font-extrabold text-emerald-600">
                                        ₦{Number(creditline.current_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-zinc-400">
                                Select customer to view credit status
                            </div>
                        )}
                    </Card>
                </div>

            </div>
        </div>
    );
};

export default EditInvoice;